import { useState } from 'react';
import { ArrowLeft, Home } from 'lucide-react';
import { Button } from '@etherisc/ui-kit';
import { CoverageSummaryView } from './CoverageSummaryView';
import { CoverageDetailedView } from './CoverageDetailedView';
import { CoverageListView } from './CoverageListView';
import type { 
  CoverageItem, 
  CoverageFilters,
  CoverageDetailView,
  CoverageSummary,
  RegionalCoverageSummary
} from '../../types/coverage';

// Navigation types
export type CoverageViewType = 'summary' | 'list' | 'detail';

export interface CoverageNavigationState {
  currentView: CoverageViewType;
  selectedCoverageId?: string;
  breadcrumbs: Array<{
    label: string;
    view: CoverageViewType;
    id?: string;
  }>;
}

interface CoverageRouterProps {
  // Data props
  summaryData: CoverageSummary | null;
  regionalSummaries: RegionalCoverageSummary[];
  listData: CoverageItem[];
  detailData: CoverageDetailView | null;
  
  // Loading states
  summaryLoading?: boolean;
  listLoading?: boolean;
  detailLoading?: boolean;
  
  // Error states

  listError?: string | null;
  detailError?: string | null;
  
  // Event handlers
  onViewDetails?: (item: CoverageItem) => void;
  onRefreshSummary?: () => void;
  onRefreshList?: () => void;
  onFiltersChange?: (filters: Partial<CoverageFilters>) => void;
  onExport?: (selectedItems: CoverageItem[]) => void;
  
  // Initial view
  initialView?: CoverageViewType;
  initialCoverageId?: string;
  
  className?: string;
}

export function CoverageRouter({
  summaryData,
  regionalSummaries,
  listData,
  detailData,
  summaryLoading = false,
  listLoading = false,
  detailLoading = false,
  listError = null,
  detailError = null,
  onViewDetails,
  onRefreshSummary,
  onRefreshList,
  onFiltersChange,
  onExport,
  initialView = 'summary',
  initialCoverageId,
  className = ''
}: CoverageRouterProps) {
  const [navigationState, setNavigationState] = useState<CoverageNavigationState>({
    currentView: initialView,
    selectedCoverageId: initialCoverageId,
    breadcrumbs: [
      { label: 'Coverage', view: 'summary' }
    ]
  });

  // Navigation functions
  const navigateToSummary = () => {
    setNavigationState({
      currentView: 'summary',
      selectedCoverageId: undefined,
      breadcrumbs: [
        { label: 'Coverage', view: 'summary' }
      ]
    });
  };

  const navigateToList = () => {
    setNavigationState({
      currentView: 'list',
      selectedCoverageId: undefined,
      breadcrumbs: [
        { label: 'Coverage', view: 'summary' },
        { label: 'All Coverage Data', view: 'list' }
      ]
    });
  };

  const navigateToDetail = (item: CoverageItem) => {
    setNavigationState({
      currentView: 'detail',
      selectedCoverageId: item.id,
      breadcrumbs: [
        { label: 'Coverage', view: 'summary' },
        { label: 'All Coverage Data', view: 'list' },
        { label: item.name, view: 'detail', id: item.id }
      ]
    });
    
    if (onViewDetails) {
      onViewDetails(item);
    }
  };

  const navigateTo = (view: CoverageViewType, id?: string) => {
    switch (view) {
      case 'summary':
        navigateToSummary();
        break;
      case 'list':
        navigateToList();
        break;
      case 'detail':
        if (id) {
          const item = listData.find(item => item.id === id);
          if (item) {
            navigateToDetail(item);
          }
        }
        break;
    }
  };

  const handleBack = () => {
    const { breadcrumbs } = navigationState;
    if (breadcrumbs.length > 1) {
      const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
      navigateTo(previousBreadcrumb.view, previousBreadcrumb.id);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation
        breadcrumbs={navigationState.breadcrumbs}
        onNavigate={navigateTo}
        onBack={handleBack}
        showBack={navigationState.breadcrumbs.length > 1}
      />

      {/* Route to appropriate view */}
      {navigationState.currentView === 'summary' && (
                  <CoverageSummaryView
            summaryData={summaryData}
            regionalSummaries={regionalSummaries}
            loading={summaryLoading}
            onRefresh={onRefreshSummary}
          />
      )}

      {navigationState.currentView === 'list' && (
        <CoverageListView
          data={listData}
          loading={listLoading}
          error={listError}
          onViewDetails={navigateToDetail}
          onRefresh={onRefreshList}
          onFiltersChange={onFiltersChange}
          onExport={onExport}
        />
      )}

      {navigationState.currentView === 'detail' && (
        <CoverageDetailedView
          coverage={detailData}
          loading={detailLoading}
          error={detailError}
          onBack={handleBack}
        />
      )}

      {/* Quick Navigation Actions */}
      <QuickNavigationActions
        currentView={navigationState.currentView}
        onNavigateToSummary={navigateToSummary}
        onNavigateToList={navigateToList}
      />
    </div>
  );
}

// Breadcrumb Navigation Component
interface BreadcrumbNavigationProps {
  breadcrumbs: CoverageNavigationState['breadcrumbs'];
  onNavigate: (view: CoverageViewType, id?: string) => void;
  onBack: () => void;
  showBack: boolean;
}

function BreadcrumbNavigation({ 
  breadcrumbs, 
  onNavigate, 
  onBack, 
  showBack 
}: BreadcrumbNavigationProps) {
  return (
    <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
      {/* Back Button */}
      {showBack && (
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      )}

      {/* Breadcrumb Trail */}
      <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <span className="mx-2 text-gray-400">/</span>
              )}
              {index === breadcrumbs.length - 1 ? (
                <span className="font-medium text-gray-900">{crumb.label}</span>
              ) : (
                <button
                  onClick={() => onNavigate(crumb.view, crumb.id)}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {crumb.label}
                </button>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}

// Quick Navigation Actions Component
interface QuickNavigationActionsProps {
  currentView: CoverageViewType;
  onNavigateToSummary: () => void;
  onNavigateToList: () => void;
}

function QuickNavigationActions({ 
  currentView, 
  onNavigateToSummary, 
  onNavigateToList 
}: QuickNavigationActionsProps) {
  return (
    <div className="fixed bottom-6 right-6 flex flex-col gap-2">
      {currentView !== 'summary' && (
        <Button
          onClick={onNavigateToSummary}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 shadow-lg"
        >
          <Home className="w-4 h-4" />
          Summary
        </Button>
      )}
      
      {currentView !== 'list' && (
        <Button
          onClick={onNavigateToList}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 shadow-lg"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          All Data
        </Button>
      )}
    </div>
  );
} 